import { takeWishlistLoginReturn } from './wishlist-login-return'

/** Where a shopper lands once Omneo has authenticated them. */
const ACCOUNT_PATH = '/account'

/**
 * Checkout is served from a different origin, so the drawer never renders there and this only ever
 * matches the on-domain step that precedes it. `/checkouts/` covers the one-page checkout paths
 * Shopify does serve on the shop domain.
 */
const CHECKOUT_PATHS = ['/checkout', '/checkouts/']

/** There is no ambient declaration for the state blob `gm-state.liquid` writes into `<head>`. */
type WindowWithState = typeof window & {
  GM_STATE?: { routes?: { cart_url?: string } }
}

/** True when `path` is the cart page, allowing for a locale prefix such as `/en-nz/cart`. */
const isCartPath = (path: string): boolean => {
  const cartUrl = (window as WindowWithState).GM_STATE?.routes?.cart_url || '/cart'
  return path === cartUrl || path.startsWith(`${cartUrl}?`) || /^(\/[a-z]{2}(-[a-z]{2})?)?\/cart\/?$/i.test(path)
}

/**
 * The param `account-login-redirect.liquid` carries across when Shopify (or checkout) sends a
 * shopper to /account/login with a return_url. Those routes replace themselves with the drawer on
 * the homepage, so without this the original destination is lost before the drawer ever opens.
 */
const RETURN_PARAM = 'return_to'

/** Same-origin relative paths only — '//host' is a protocol-relative URL, not a local path. */
const isSafeReturnPath = (path: string | null): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//')

const takeReturnParam = (): string | null => {
  const returnTo = new URLSearchParams(window.location.search).get(RETURN_PARAM)
  return isSafeReturnPath(returnTo) ? returnTo : null
}

/**
 * The current path, minus the params that only exist to drive the drawer. Returning
 * `?modal=account` would re-open the drawer the moment the shopper lands back on the cart.
 */
const currentPathWithoutDrawerParams = (): string => {
  const params = new URLSearchParams(window.location.search)
  params.delete('modal')
  params.delete(RETURN_PARAM)
  const search = params.toString()
  return search ? `${window.location.pathname}?${search}` : window.location.pathname
}

const isCheckoutPath = (path: string): boolean =>
  CHECKOUT_PATHS.some((checkoutPath) => path.startsWith(checkoutPath) || path.includes(checkoutPath))

/**
 * Post-login destination, in precedence order:
 *
 *   1. the page the wishlist drawer recorded, so saving a product is not interrupted;
 *   2. a return_to carried over from /account/login, which is how a shopper arrives from checkout;
 *   3. the cart or checkout, when the shopper logged in from there and is mid-purchase;
 *   4. the account dashboard.
 *
 * Rule 4 is the change from the previous behaviour, where the email magic link passed
 * `window.location.pathname` and returned the shopper to whatever page they happened to be on.
 * The SMS path already defaulted to the dashboard via `mobileLogin`.
 */
export const resolveLoginRedirect = (): string => {
  const wishlistReturn = takeWishlistLoginReturn()
  if (wishlistReturn) return wishlistReturn

  const returnParam = takeReturnParam()
  if (returnParam) return returnParam

  if (isCartPath(window.location.pathname) || isCheckoutPath(window.location.pathname)) {
    return currentPathWithoutDrawerParams()
  }

  return ACCOUNT_PATH
}
